import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { useAuth } from '../auth/AuthProvider';
import { AppLink } from '../components/Nav';
import { PlusIcon, UsersIcon } from '../components/icons';
import {
  Badge,
  Banner,
  Button,
  CopyButton,
  EmptyState,
  ErrorState,
  ErrorText,
  Input,
  Loading,
  PageHeader,
  Select,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { countLabel, shortDate } from '../lib/format';
import type { OrgMember, OrgSummary, Role } from '../lib/types';
import { qk } from '../queries/keys';
import {
  atLeast,
  fetchOrg,
  inviteMember,
  roleDescription,
  roleLabel,
  setMemberRole,
} from '../queries/orgs';

/**
 * Who is on the team, and what each of them can do.
 *
 * The API is the authority on who may change whom: an admin trying to demote
 * an owner gets a 403 with a sentence in it, and that sentence is what we show.
 * The only rule duplicated here is hiding the controls on your own row, because
 * offering someone a button that removes themselves is just unkind.
 */

const ROLES: Role[] = ['user', 'admin', 'owner'];

function roleTone(role: Role): 'info' | 'neutral' {
  return role === 'user' ? 'neutral' : 'info';
}

function MemberRow({
  orgId,
  member,
  isSelf,
}: {
  orgId: string;
  member: OrgMember;
  isSelf: boolean;
}) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: qk.org(orgId) });
    void client.invalidateQueries({ queryKey: qk.orgs() });
  };

  const changeRole = useMutation({
    mutationFn: (role: Role) => setMemberRole(orgId, member.user.id, role),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: () => setMemberRole(orgId, member.user.id, null),
    onSuccess: () => {
      setConfirming(false);
      invalidate();
    },
  });

  const busy = changeRole.isPending || remove.isPending;
  const failure = changeRole.error ?? remove.error;

  return (
    <li className="border-b border-hairline py-3.5 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-body text-chalk">
            <span className="truncate">{member.user.name ?? member.user.email}</span>
            <Badge tone={roleTone(member.role)}>{roleLabel(member.role)}</Badge>
            {isSelf ? <Badge tone="neutral">You</Badge> : null}
          </p>
          <p className="mt-0.5 truncate text-caption text-chalk-faint">{member.user.email}</p>
          <p className="mt-0.5 text-caption text-chalk-faint">
            Joined {shortDate(member.createdAt)}
          </p>
        </div>

        {isSelf ? (
          <p className="text-caption text-chalk-faint">
            Another owner can change your role.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={member.role}
              disabled={busy}
              aria-label={`Role for ${member.user.email}`}
              onChange={(event) => changeRole.mutate(event.target.value as Role)}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {roleLabel(role)}
                </option>
              ))}
            </Select>

            {confirming ? (
              <span className="flex items-center gap-2">
                <span className="text-caption text-chalk-dim">Remove?</span>
                <Button
                  variant="danger"
                  size="sm"
                  busy={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  Yes, remove
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Keep
                </Button>
              </span>
            ) : (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
                Remove
              </Button>
            )}
          </div>
        )}
      </div>

      {failure ? (
        <div className="mt-2">
          <ErrorText>{errorMessage(failure)}</ErrorText>
        </div>
      ) : null}
    </li>
  );
}

function InviteForm({ orgId }: { orgId: string }) {
  const client = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [invalid, setInvalid] = useState<string | null>(null);
  const [sent, setSent] = useState<{ email: string; acceptUrl: string } | null>(null);

  const invite = useMutation({
    mutationFn: (input: { email: string; role: Role }) =>
      inviteMember(orgId, input.email, input.role),
    onSuccess: (data, input) => {
      setSent({ email: input.email, acceptUrl: data.invite.acceptUrl });
      setEmail('');
      void client.invalidateQueries({ queryKey: qk.org(orgId) });
    },
  });

  const submit = () => {
    setInvalid(null);
    setSent(null);
    const trimmed = email.trim();
    if (!trimmed.includes('@') || trimmed.length < 5) {
      setInvalid('That does not look like an email address.');
      return;
    }
    invite.mutate({ email: trimmed, role });
  };

  return (
    <form
      className="card p-4 sm:p-5"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <h2 className="text-h4 text-chalk">Invite someone</h2>
      <p className="mt-1 text-caption text-chalk-dim">
        You get a link to send them. Nothing changes on your bill until they accept.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
        <Input
          label="Email address"
          type="email"
          placeholder="teammate@company.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Select
          label="Role"
          value={role}
          onChange={(event) => setRole(event.target.value as Role)}
        >
          {ROLES.map((option) => (
            <option key={option} value={option}>
              {roleLabel(option)}
            </option>
          ))}
        </Select>
      </div>

      <p className="mt-2 text-caption text-chalk-faint">{roleDescription(role)}</p>

      {invalid ? (
        <div className="mt-3">
          <ErrorText>{invalid}</ErrorText>
        </div>
      ) : null}
      {invite.isError ? (
        <div className="mt-3">
          <ErrorText>{errorMessage(invite.error)}</ErrorText>
        </div>
      ) : null}
      {sent ? (
        <div className="mt-3">
          <Banner tone="go" title={`Invitation ready for ${sent.email}`}>
            <span className="block break-all font-mono text-caption">{sent.acceptUrl}</span>
            <span className="mt-1 block">
              Send them this link. It works for a few days, and only {sent.email} can accept
              it.
            </span>
            <span className="mt-2 block">
              <CopyButton
                value={sent.acceptUrl}
                label="Copy link"
                copiedLabel="Copied"
                variant="secondary"
                size="sm"
              />
            </span>
          </Banner>
        </div>
      ) : null}

      <Button type="submit" variant="primary" className="mt-4" busy={invite.isPending}>
        <PlusIcon size={14} />
        Send invitation
      </Button>
    </form>
  );
}

function Members({ org }: { org: OrgSummary }) {
  const { user } = useAuth();
  const detail = useQuery({ queryKey: qk.org(org.id), queryFn: () => fetchOrg(org.id) });

  const viewerEmail = user?.email?.toLowerCase() ?? null;

  return (
    <>
      <PageHeader
        title="Members"
        subtitle={
          <span>
            {org.name} · <span className="tnum">{countLabel(org.seatCount, 'seat')}</span>.
            You&rsquo;re billed per active member.
          </span>
        }
        actions={
          <AppLink to="/org/$slug" params={{ slug: org.slug }}>
            <Button>Back to workspace</Button>
          </AppLink>
        }
      />

      {detail.isPending ? <Loading label="Loading the team" /> : null}

      {detail.isError ? (
        <ErrorState
          title="Could not load the member list"
          message={errorMessage(detail.error)}
          onRetry={() => void detail.refetch()}
        />
      ) : null}

      {detail.data ? (
        <div className="space-y-6">
          <section className="card p-4 sm:p-5">
            <h2 className="text-h4 text-chalk">
              {countLabel(detail.data.memberships.length, 'member')}
            </h2>
            <ul className="mt-2">
              {detail.data.memberships.map((member) => (
                <MemberRow
                  key={member.user.id}
                  orgId={org.id}
                  member={member}
                  isSelf={
                    viewerEmail !== null && member.user.email.toLowerCase() === viewerEmail
                  }
                />
              ))}
            </ul>
          </section>

          <section className="card p-4 sm:p-5">
            <h2 className="text-h4 text-chalk">Pending invitations</h2>
            {detail.data.invites.length === 0 ? (
              <p className="mt-2 text-body text-chalk-dim">
                Nobody is waiting on an invitation right now.
              </p>
            ) : (
              <ul className="mt-2">
                {detail.data.invites.map((invitation) => (
                  <li
                    key={invitation.id}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline py-3 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-body text-chalk">{invitation.email}</p>
                      <p className="text-caption text-chalk-faint">
                        Sent {shortDate(invitation.createdAt)} · expires{' '}
                        {shortDate(invitation.expiresAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={roleTone(invitation.role)}>{roleLabel(invitation.role)}</Badge>
                      {invitation.acceptUrl ? (
                        <CopyButton
                          value={invitation.acceptUrl}
                          label="Copy link"
                          copiedLabel="Copied"
                          variant="ghost"
                          size="sm"
                        />
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <InviteForm orgId={org.id} />

          <section className="card p-4 sm:p-5">
            <h2 className="text-h4 text-chalk">What the roles mean</h2>
            <dl className="mt-3 space-y-2.5">
              {ROLES.map((role) => (
                <div key={role} className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                  <dt className="w-24 shrink-0">
                    <Badge tone={roleTone(role)}>{roleLabel(role)}</Badge>
                  </dt>
                  <dd className="text-body text-chalk-dim">{roleDescription(role)}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      ) : null}
    </>
  );
}

export function OrgMembersPage({ slug }: { slug: string }) {
  const { orgs, loading, error, refetch } = useWorkspace();

  if (loading) return <Loading label="Loading your workspaces" />;

  if (error) {
    return (
      <ErrorState
        title="Could not load your workspaces"
        message={errorMessage(error)}
        onRetry={refetch}
      />
    );
  }

  const org = orgs.find((candidate) => candidate.slug === slug);

  if (!org) {
    return (
      <>
        <PageHeader title="Members" />
        <EmptyState
          title="You are not a member of this workspace"
          icon={<UsersIcon size={26} />}
          action={
            <AppLink to="/">
              <Button variant="primary">Go to your library</Button>
            </AppLink>
          }
        >
          <p>
            The address may be wrong, or your membership may have been removed. Ask a teammate to
            send you a fresh invitation.
          </p>
        </EmptyState>
      </>
    );
  }

  if (!atLeast(org.role, 'admin')) {
    return (
      <>
        <PageHeader title="Members" subtitle={org.name} />
        <Banner
          tone="info"
          title="Members are managed by admins"
          action={
            <AppLink to="/org/$slug" params={{ slug: org.slug }}>
              <Button size="sm">Back to workspace</Button>
            </AppLink>
          }
        >
          You can record into this workspace and watch everything in it, but adding people, changing
          roles, and removing members are admin jobs. Ask an admin or the owner if you need someone
          added.
        </Banner>
      </>
    );
  }

  return <Members key={org.id} org={org} />;
}
